<script>
// utils and composables
import { login } from '../../api/auth';
import { mapGetters } from 'vuex';
import { parseBoolean } from '@chatwoot/utils';
import { useAlert } from 'dashboard/composables';
import { required, email } from '@vuelidate/validators';
import { useVuelidate } from '@vuelidate/core';
import { SESSION_STORAGE_KEYS } from 'dashboard/constants/sessionStorage';
import SessionStorage from 'shared/helpers/sessionStorage';
import { useBranding } from 'shared/composables/useBranding';

// components
import FormInput from '../../components/Form/Input.vue';
import GoogleOAuthButton from '../../components/GoogleOauth/Button.vue';
import Spinner from 'shared/components/Spinner.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import FluentIcon from 'shared/components/FluentIcon/Index.vue';

const IMPERSONATION_URL_SEARCH_KEY = 'impersonation';

export default {
  components: {
    FormInput,
    GoogleOAuthButton,
    Spinner,
    NextButton,
    FluentIcon,
  },
  props: {
    ssoAuthToken: { type: String, default: '' },
    ssoAccountId: { type: String, default: '' },
    ssoConversationId: { type: String, default: '' },
    email: { type: String, default: '' },
    authError: { type: String, default: '' },
  },
  setup() {
    const { replaceInstallationName } = useBranding();
    return {
      replaceInstallationName,
      v$: useVuelidate(),
    };
  },
  data() {
    return {
      credentials: {
        email: '',
        password: '',
      },
      loginApi: {
        message: '',
        showLoading: false,
        hasErrored: false,
      },
      isPasswordVisible: false,
      error: '',
    };
  },
  validations() {
    return {
      credentials: {
        password: { required },
        email: { required, email },
      },
    };
  },
  computed: {
    ...mapGetters({ globalConfig: 'globalConfig/get' }),
    showGoogleOAuth() {
      return Boolean(window.chatwootConfig.googleOAuthClientId);
    },
    showSignupLink() {
      return parseBoolean(window.chatwootConfig.signupEnabled);
    },
  },
  created() {
    if (this.ssoAuthToken) {
      this.submitLogin();
    }
    if (this.authError) {
      let translationKey = 'LOGIN.API.UNAUTH';
      if (this.authError === 'no-account-found') {
        translationKey = 'LOGIN.OAUTH.NO_ACCOUNT_FOUND';
      } else if (this.authError === 'business-account-only') {
        translationKey = 'LOGIN.OAUTH.BUSINESS_ACCOUNTS_ONLY';
      }
      useAlert(this.$t(translationKey));
      this.requestIdleCallbackPolyfill(() => {
        const { query } = this.$route;
        this.$router.replace({ query: { ...query, error: undefined } });
      });
    }
  },
  methods: {
    getParticleStyle() {
      const size = Math.random() * 4 + 1;
      const left = Math.random() * 100;
      const animationDelay = Math.random() * 20;
      const animationDuration = Math.random() * 20 + 10;
      return {
        width: `${size}px`,
        height: `${size}px`,
        left: `${left}%`,
        animationDelay: `${animationDelay}s`,
        animationDuration: `${animationDuration}s`,
      };
    },
    requestIdleCallbackPolyfill(callback) {
      if (window.requestIdleCallback) {
        window.requestIdleCallback(callback);
      } else {
        setTimeout(callback, 0);
      }
    },
    showAlertMessage(message) {
      this.loginApi.showLoading = false;
      this.loginApi.message = message;
      useAlert(this.loginApi.message);
    },
    handleImpersonation() {
      const urlParams = new URLSearchParams(window.location.search);
      const impersonation = urlParams.get(IMPERSONATION_URL_SEARCH_KEY);
      if (impersonation) {
        SessionStorage.set(SESSION_STORAGE_KEYS.IMPERSONATION_USER, true);
      }
    },
    submitLogin() {
      this.loginApi.hasErrored = false;
      this.loginApi.showLoading = true;

      const credentials = {
        email: this.email
          ? decodeURIComponent(this.email)
          : this.credentials.email,
        password: this.credentials.password,
        sso_auth_token: this.ssoAuthToken,
        ssoAccountId: this.ssoAccountId,
        ssoConversationId: this.ssoConversationId,
      };

      login(credentials)
        .then(() => {
          this.handleImpersonation();
          this.showAlertMessage(this.$t('LOGIN.API.SUCCESS_MESSAGE'));
        })
        .catch(response => {
          if (this.email) {
            window.location = '/app/login';
          }
          this.loginApi.hasErrored = true;
          this.showAlertMessage(
            response?.message || this.$t('LOGIN.API.UNAUTH')
          );
        });
    },
    submitFormLogin() {
      if (this.v$.credentials.email.$invalid && !this.email) {
        this.showAlertMessage(this.$t('LOGIN.EMAIL.ERROR'));
        return;
      }
      this.submitLogin();
    },
    togglePasswordVisibility() {
      this.isPasswordVisible = !this.isPasswordVisible;
    },
  },
};
</script>

<template>
  <main class="relative flex h-screen overflow-hidden new-theme-bg">
    <!-- Grain texture overlay -->
    <div class="grain-overlay" />
    <!-- Left Panel: Branding / Illustration -->
    <section
      class="hidden lg:flex flex-col items-center w-1/2 p-16 pt-64 text-center relative overflow-hidden h-screen z-10 animate-slide-from-left"
    >
      <!-- Vertical Divider Line -->
      <div
        class="absolute right-0 top-1/2 -translate-y-1/2 h-3/4 w-px bg-gradient-to-b from-transparent via-white/50 to-transparent z-20"
      />

      <div
        class="relative z-10 w-full flex flex-col items-center justify-center space-y-8"
      >
        <img
          :src="globalConfig.logoDark || globalConfig.logo"
          :alt="globalConfig.installationName"
          class="h-14 drop-shadow-2xl filter brightness-110 animate-float-in"
        />

        <p
          class="text-slate-50 text-xl leading-relaxed font-light tracking-wide drop-shadow-[0_0_6px_rgba(255,255,255,0.3)] max-w-md animate-slide-up-delayed"
        >
          {{ $t('LOGIN.BRANDING.TAGLINE') }}<br />
          <span class="text-slate-100 font-normal">
            {{ $t('LOGIN.BRANDING.SUBTITLE') }}
          </span>
        </p>

        <!-- Glowing orb effect -->
        <div class="relative mt-8">
          <div
            class="w-64 h-64 rounded-full bg-gradient-to-tr from-pink-500/20 via-blue-500/30 to-cyan-500/20 blur-3xl opacity-60 animate-pulse-slow"
          />
          <div
            class="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-32 h-32 rounded-full bg-gradient-to-tr from-pink-400/40 to-blue-400/40 blur-2xl animate-pulse-slower"
          />
        </div>
      </div>
    </section>

    <!-- Right Panel: Login Card -->
    <section
      class="flex flex-col justify-center items-center w-full lg:w-1/2 px-3 sm:px-4 md:px-6 lg:px-12 py-6 sm:py-8 md:py-12 lg:py-16 relative overflow-hidden h-screen z-10 animate-slide-from-right"
    >
      <!-- Mobile Logo and Brand Name (visible only on mobile, outside the box) -->
      <div
        class="lg:hidden flex flex-col items-center mb-4 sm:mb-6 animate-fade-in-up w-full max-w-sm sm:max-w-md px-4"
      >
        <img
          :src="globalConfig.logoDark || globalConfig.logo"
          :alt="globalConfig.installationName"
          class="h-10 sm:h-12 mb-2 sm:mb-3 drop-shadow-2xl filter brightness-110"
        />
        <p
          class="text-slate-50 text-xs sm:text-sm leading-relaxed font-light tracking-wide drop-shadow-[0_0_6px_rgba(255,255,255,0.3)] text-center"
        >
          {{ $t('LOGIN.BRANDING.TAGLINE') }}<br />
          <span class="text-slate-100 font-normal">
            {{ $t('LOGIN.BRANDING.SUBTITLE') }}
          </span>
        </p>
      </div>

      <div
        class="w-full max-w-sm sm:max-w-md bg-white/5 backdrop-blur-4xl rounded-xl sm:rounded-2xl md:rounded-3xl border border-white/10 p-4 sm:p-6 md:p-8 lg:p-10 transition-all duration-300 hover:bg-white/10 hover:border-white/20 mx-2 sm:mx-4 md:mx-0 shadow-2xl shadow-black/40"
        :class="{ 'animate-wiggle': loginApi.hasErrored }"
      >
        <div class="text-center mb-6 sm:mb-8 animate-fade-in-up">
          <h2
            class="text-lg sm:text-xl md:text-2xl lg:text-3xl font-bold text-white mb-2 sm:mb-3 md:mb-4 tracking-wide"
          >
            {{ replaceInstallationName($t('LOGIN.TITLE')) }}
          </h2>
          <p v-if="showSignupLink" class="text-xs sm:text-sm text-gray-400">
            {{ $t('COMMON.OR') }}
            <router-link
              to="auth/signup"
              class="text-purple-400 font-medium hover:text-purple-300 transition-colors"
            >
              {{ $t('LOGIN.CREATE_NEW_ACCOUNT') }}
            </router-link>
          </p>
        </div>

        <!-- Login Form -->
        <div
          v-if="!email"
          class="space-y-3 sm:space-y-4 md:space-y-6 animate-fade-in-up-delayed"
        >
          <transition name="fade-slide">
            <GoogleOAuthButton
              v-if="showGoogleOAuth"
              class="w-full mb-2 sm:mb-3 md:mb-4"
            />
          </transition>

          <form
            class="space-y-3 sm:space-y-4 md:space-y-5 transition-all duration-500"
            @submit.prevent="submitFormLogin"
          >
            <!-- Email -->
            <div class="group/input">
              <FormInput
                v-model="credentials.email"
                name="email_address"
                type="text"
                data-testid="email_input"
                :tabindex="1"
                required
                :label="$t('LOGIN.EMAIL.LABEL')"
                :placeholder="$t('LOGIN.EMAIL.PLACEHOLDER')"
                :has-error="v$.credentials.email.$error"
                class="rounded-xl focus-within:ring-2 focus-within:ring-purple-400/70 focus-within:border-purple-400/50 transition-all duration-300 bg-white/5 border-transparent text-white placeholder-gray-300"
                @input="v$.credentials.email.$touch"
              />
            </div>

            <!-- Password -->
            <div class="group/input">
              <label
                for="login-password"
                class="flex justify-between text-sm font-medium leading-6 text-n-slate-12"
              >
                {{ $t('LOGIN.PASSWORD.LABEL') }}
              </label>
              <div class="relative">
                <input
                  id="login-password"
                  v-model="credentials.password"
                  :type="isPasswordVisible ? 'text' : 'password'"
                  name="password"
                  data-testid="password_input"
                  required
                  :tabindex="2"
                  :placeholder="$t('LOGIN.PASSWORD.PLACEHOLDER')"
                  class="block w-full border-none rounded-md shadow-sm bg-n-alpha-black2 appearance-none outline outline-1 focus:outline focus:outline-1 text-n-slate-12 placeholder:text-n-slate-10 sm:text-sm sm:leading-6 px-3 py-3 pr-10 rounded-xl transition-all duration-300 text-white placeholder-gray-300"
                  :class="{
                    'outline-n-ruby-8 dark:outline-n-ruby-8 hover:outline-n-ruby-9 dark:hover:outline-n-ruby-9 disabled:outline-n-ruby-8 dark:disabled:outline-n-ruby-8':
                      v$.credentials.password.$error,
                    'outline-n-weak dark:outline-n-weak hover:outline-n-slate-6 dark:hover:outline-n-slate-6 focus:outline-n-brand dark:focus:outline-n-brand':
                      !v$.credentials.password.$error,
                  }"
                  @input="v$.credentials.password.$touch"
                />
                <button
                  type="button"
                  class="absolute inset-y-0 right-3 z-10 inline-flex items-center text-gray-300 hover:text-white transition-colors"
                  :aria-label="
                    isPasswordVisible ? 'Hide password' : 'Show password'
                  "
                  @click="togglePasswordVisibility"
                >
                  <FluentIcon
                    size="18"
                    :icon="
                      isPasswordVisible
                        ? 'eye-hide-outline'
                        : 'eye-show-outline'
                    "
                  />
                </button>
              </div>
              <p
                v-if="!globalConfig.disableUserProfileUpdate"
                class="text-right mt-2"
              >
                <router-link
                  to="auth/reset/password"
                  class="text-sm text-white hover:text-gray-200 hover:underline"
                  tabindex="4"
                >
                  {{ $t('LOGIN.FORGOT_PASSWORD') }}
                </router-link>
              </p>
            </div>

            <!-- Submit -->
            <NextButton
              lg
              type="submit"
              data-testid="submit_button"
              class="w-full mt-3 sm:mt-4 md:mt-6 bg-gradient-to-r from-violet-600 via-violet-500 to-violet-600 text-white"
              :tabindex="3"
              :label="$t('LOGIN.SUBMIT')"
              :disabled="loginApi.showLoading"
              :is-loading="loginApi.showLoading"
            />
          </form>
        </div>

        <!-- Spinner for SSO -->
        <div
          v-else
          class="flex flex-col items-center justify-center h-40 gap-3"
        >
          <Spinner color-scheme="primary" size="lg" />
          <p class="text-n-slate-11 text-sm animate-pulse">
            {{ $t('LOGIN.SSO_LOADING') }}
          </p>
        </div>
      </div>

      <footer
        class="mt-6 sm:mt-8 lg:mt-10 text-center text-xs text-n-slate-10 px-4 animate-fade-in-delayed"
      >
        {{ $t('LOGIN.BRANDING.COPYRIGHT') }} {{ new Date().getFullYear() }}
        {{ globalConfig.installationName }}
        <span class="text-n-slate-8 hidden sm:inline">
          {{ $t('LOGIN.BRANDING.SEPARATOR') }}
        </span>
        <br class="sm:hidden" />
        <span class="text-n-slate-8">
          {{ $t('LOGIN.BRANDING.FOOTER_TEXT') }}
        </span>
      </footer>
    </section>
  </main>
</template>

<style scoped>
.fade-slide-enter-active {
  transition: all 0.6s ease;
}
.fade-slide-enter-from {
  opacity: 0;
  transform: translateY(10px);
}
.fade-slide-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}

/* Force white text color for form inputs */
:deep(.group\/input input) {
  color: white !important;
}

:deep(.group\/input input::placeholder) {
  color: #9ca3af !important; /* gray-400 */
}

/* Force white color for Email and Password labels */
:deep(.group\/input label) {
  color: white !important;
}

/* Password toggle button positioning */
:deep(.group\/input .relative) {
  position: relative;
}

:deep(.group\/input input) {
  padding-right: 0.75rem !important;
  padding-top: 0.75rem !important;
  padding-bottom: 0.75rem !important;
  padding-left: 0.75rem !important;
  border-color: transparent !important;
  outline-color: transparent !important;
}

:deep(.group\/input input:hover) {
  outline-color: transparent !important;
}

:deep(.group\/input input:focus) {
  border-color: rgb(192 132 252 / 0.5) !important;
  outline-color: rgb(192 132 252 / 0.5) !important;
}

/* Particle animation */
.particle-container {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  overflow: hidden;
}

.particle {
  position: absolute;
  background: linear-gradient(
    45deg,
    rgba(6, 182, 212, 0.6),
    rgba(59, 130, 246, 0.4)
  );
  border-radius: 50%;
  animation: float linear infinite;
  opacity: 0.7;
}

@keyframes float {
  0% {
    transform: translateY(100vh) rotate(0deg);
    opacity: 0;
  }
  10% {
    opacity: 0.7;
  }
  90% {
    opacity: 0.7;
  }
  100% {
    transform: translateY(-100px) rotate(360deg);
    opacity: 0;
  }
}

/* Grain animation - enhanced for new theme */
@keyframes grain {
  0%,
  100% {
    transform: translate(0, 0);
  }
  10% {
    transform: translate(-2%, -2%);
  }
  20% {
    transform: translate(-1%, 1%);
  }
  30% {
    transform: translate(1%, -1%);
  }
  40% {
    transform: translate(-1%, 1%);
  }
  50% {
    transform: translate(-1%, -1%);
  }
  60% {
    transform: translate(1%, 0%);
  }
  70% {
    transform: translate(0%, 1%);
  }
  80% {
    transform: translate(-1%, -1%);
  }
  90% {
    transform: translate(1%, 1%);
  }
}

/* Wiggle animation for errors */
@keyframes wiggle {
  0%,
  7% {
    transform: rotateZ(0);
  }
  15% {
    transform: rotateZ(-15deg);
  }
  20% {
    transform: rotateZ(10deg);
  }
  25% {
    transform: rotateZ(-10deg);
  }
  30% {
    transform: rotateZ(6deg);
  }
  35% {
    transform: rotateZ(-4deg);
  }
  40%,
  100% {
    transform: rotateZ(0);
  }
}

.animate-wiggle {
  animation: wiggle 0.5s ease-in-out;
}

/* Enhanced hover effects */
/* Removed input hover transform effect */

/* Removed problematic glow effect */

/* Custom gradient for radial backgrounds */
.bg-gradient-radial {
  background: radial-gradient(circle, var(--tw-gradient-stops));
}

/* New theme background - image with grain texture */
.new-theme-bg {
  background: url('/assets/images/auth/login-bg.png') center center / cover
    no-repeat;
  position: relative;
}

/* Grain texture overlay - subtle enhancement for image background */
.grain-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  opacity: 0;
  background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 400 400' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='2.5' numOctaves='6' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E");
  background-size: 120px 120px;
  animation: grain 4s steps(10) infinite;
  mix-blend-mode: overlay;
  z-index: 1;
}

/* Alternative grain effect using CSS for better browser support */
.grain-overlay::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-image: repeating-linear-gradient(
      0deg,
      rgba(255, 255, 255, 0.08) 0px,
      transparent 1px,
      transparent 2px,
      rgba(255, 255, 255, 0.08) 3px
    ),
    repeating-linear-gradient(
      90deg,
      rgba(255, 255, 255, 0.08) 0px,
      transparent 1px,
      transparent 2px,
      rgba(255, 255, 255, 0.08) 3px
    );
  opacity: 0;
  pointer-events: none;
}

/* Mobile optimizations */
@media (max-width: 640px) {
  .particle {
    display: none; /* Hide particles on mobile for better performance */
  }

  .group:hover .group\/input {
    transform: none; /* Disable hover transforms on mobile */
  }

  .group:hover {
    transform: none; /* Disable hover scale on mobile */
  }

  /* Reduce animation intensity on mobile */
  .animate-pulse {
    animation-duration: 3s;
  }

  /* Optimize backdrop blur for mobile */
  .backdrop-blur-xl {
    backdrop-filter: blur(8px);
  }
}

/* Tablet optimizations */
@media (min-width: 641px) and (max-width: 1024px) {
  .particle {
    opacity: 0.5; /* Reduce particle opacity on tablets */
  }
}

/* Attractive Page Animations */
@keyframes slideFromLeft {
  from {
    opacity: 0;
    transform: translateX(-100%);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}

@keyframes slideFromRight {
  from {
    opacity: 0;
    transform: translateX(100%);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

@keyframes floatIn {
  from {
    opacity: 0;
    transform: translateY(-30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes slideUpDelayed {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes scaleIn {
  from {
    opacity: 0;
    transform: scale(0.9);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes pulseSlow {
  0%,
  100% {
    opacity: 0.6;
    transform: scale(1);
  }
  50% {
    opacity: 0.8;
    transform: scale(1.05);
  }
}

@keyframes pulseSlower {
  0%,
  100% {
    opacity: 0.4;
    transform: scale(1);
  }
  50% {
    opacity: 0.6;
    transform: scale(1.08);
  }
}

.animate-slide-from-left {
  animation: slideFromLeft 1s ease-out;
}

.animate-slide-from-right {
  animation: slideFromRight 1s ease-out;
}

.animate-fade-in {
  animation: fadeIn 1s ease-out;
}

.animate-float-in {
  animation: floatIn 0.8s ease-out 1s both;
}

.animate-slide-up-delayed {
  animation: slideUpDelayed 0.8s ease-out 1.2s both;
}

.animate-scale-in {
  animation: scaleIn 0.8s ease-out;
}

.animate-fade-in-up {
  animation: fadeInUp 0.6s ease-out 1s both;
}

.animate-fade-in-up-delayed {
  animation: fadeInUp 0.6s ease-out 1.2s both;
}

.animate-fade-in-delayed {
  animation: fadeIn 0.8s ease-out 1.4s both;
}

.animate-pulse-slow {
  animation: pulseSlow 4s ease-in-out infinite;
}

.animate-pulse-slower {
  animation: pulseSlower 6s ease-in-out infinite;
}
</style>
